"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in duration-500">
        <div className="size-16 bg-secondary/10 text-secondary rounded-full flex items-center justify-center mb-6">
          <svg className="size-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-2xl font-default font-semibold mb-2 text-foreground">Accesso Autorizzato</h3>
        <p className="text-muted-foreground mb-8">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-none px-8 border-secondary text-secondary hover:bg-secondary/10">
          Invia Nuovo Protocollo
        </Button>
      </div>;
  }
  const inputStyles = "border-x-transparent border-t-transparent border-b-border rounded-none shadow-none focus-visible:ring-0 focus-visible:border-b-secondary focus-visible:border-x-transparent focus-visible:border-t-transparent px-0 bg-[#050507] text-foreground placeholder:text-muted-foreground/30 transition-colors duration-300";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Inserire il nome completo.",
      minLength: {
        value: 2,
        message: "Il nome deve contenere almeno 2 caratteri."
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase tracking-widest text-primary font-default font-bold">
              Nome Operatore / Azienda
            </FormLabel>
            <FormControl>
              <Input placeholder="NOME COGNOME" className={inputStyles} {...field} />
            </FormControl>
            <FormMessage className="text-destructive text-[10px] uppercase" />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "L'indirizzo email è obbligatorio.",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Inserire un indirizzo email valido."
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase tracking-widest text-primary font-default font-bold">
              Canale di Comunicazione (Email)
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="intelligence@azienda.it" className={inputStyles} {...field} />
            </FormControl>
            <FormMessage className="text-destructive text-[10px] uppercase" />
          </FormItem>} />

      <FormField control={control} name="sector" rules={{
      required: "Specificare il settore di business."
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase tracking-widest text-primary font-default font-bold">
              Settore Business
            </FormLabel>
            <FormControl>
              <Input placeholder="E.G. MANUFACTURING, TECH, LOGISTICS" className={inputStyles} {...field} />
            </FormControl>
            <FormMessage className="text-destructive text-[10px] uppercase" />
          </FormItem>} />

      <FormField control={control} name="region" rules={{
      required: "Specificare la regione target."
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase tracking-widest text-primary font-default font-bold">
              Regione Target (RU/KZ/BY)
            </FormLabel>
            <FormControl>
              <Input placeholder="FEDERAZIONE RUSSA / KAZAKISTAN / BIELORUSSIA" className={inputStyles} {...field} />
            </FormControl>
            <FormMessage className="text-destructive text-[10px] uppercase" />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Dettagliare i requisiti della missione.",
      minLength: {
        value: 10,
        message: "Il messaggio deve contenere almeno 10 caratteri."
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase tracking-widest text-primary font-default font-bold">
              Specifiche del Progetto
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Descrivere gli obiettivi strategici nel mercato CSI..." className={`resize-none min-h-[120px] ${inputStyles}`} {...field} />
            </FormControl>
            <FormMessage className="text-destructive text-[10px] uppercase" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-none">
          <p className="text-destructive text-xs text-center uppercase tracking-tighter">{submitError}</p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full rounded-none bg-primary text-primary-foreground hover:bg-primary/90 h-14 text-base font-default font-bold uppercase tracking-widest transition-all shadow-lg shadow-primary/10">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              TRASMISSIONE IN CORSO...
            </> : "Trasmetti Dati"}
        </Button>
      </div>
    </Form>;
}
export default function GetInTouch() {
  return <section data-section-id="236131" id="protocollo-di-contatto" className="w-full py-24 md:py-32 bg-background text-foreground relative overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="max-w-[600px] mx-auto relative">
          {}
          <div className="absolute top-0 bottom-0 -left-4 md:-left-12 w-px bg-secondary/20 hidden sm:block" />
          <div className="absolute top-0 bottom-0 -right-4 md:-right-12 w-px bg-secondary/20 hidden sm:block" />

          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-default font-bold tracking-tighter mb-4 uppercase">
              Richiedi Accesso ai Corridoi Digitali CSI
            </h2>
            <p className="text-muted-foreground font-default text-sm tracking-wide">
              Inizializza il protocollo di consultazione strategica. I nostri analisti valuteranno la fattibilità dell'infrastruttura entro 24 ore.
            </p>
          </div>

          <div className="bg-card/50 backdrop-blur-md border border-border p-8 md:p-12 rounded-none shadow-2xl">
            <FormProvider formKey="contact_form_contact" formId="contact_form_contact" sectionName="Protocollo di Contatto" defaultValues={{
            name: "",
            email: "",
            sector: "",
            region: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            sector: {
              required: true
            },
            region: {
              required: true
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Trasmetti Dati" successMessage="Protocollo ricevuto. I dati sono in fase di elaborazione crittografica. Verrete contattati tramite canale sicuro." errorMessage="Errore critico nella trasmissione. Verificare la connessione e riprovare.">
              <ContactFormInner />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}